import { BUILDER_END, availableTo, bringsEnd, missingNeeds, phaseOfNode, phasesOf, planDisable, titleCase, type PhaseKey } from '@nsb/core';
import { treeStepBand } from '../tree/job.ts';
import type { ProcessModel } from './useProcess.ts';

/**
 * Setting a job up from a list per phase (design study 31, slice 2; Jason, 2026-10-29: "simplify the whole adding of
 * nodes to a project from the top down"; picks q-setup-how C, q-setup-later B, q-r2-setup A): each phase's steps of the
 * tree the job reads, in the tree's order, ticked where the job has them. Ticking a step shows first what it needs, all
 * the way back, that the job has not got (core `missingNeeds`), each landing where it goes (core `landingPhase`), late
 * where its phase has closed; nothing is added until then.
 */

/** A step of the tree in its phase's list. */
export interface PhaseRow {
  key: string;
  title: string;
  /** Whose it is, in its band's word (bands.ts), as its tile says it; blank for a gate that happens. */
  who: string;
  picture: string | null;
  /** On the job, left out of it (filed: ticking puts it back), or not on it. */
  state: 'on' | 'left out' | 'off';
  /** On the job and unable to go (a gate passed, a step done or under way, core `planDisable`): why, in words. */
  blocked: string | null;
  /** The step of the job it is, when on it: what unticking files away. */
  stepId: string | null;
  /** Added by name, as many as the job needs (a named copy): a name and Add, never a tick. */
  repeatable: boolean;
}

/** A phase's list: its name, how many of its steps the job has, and whether it is the phase under way (open). */
export interface PhaseGroup {
  key: PhaseKey;
  name: string;
  on: number;
  of: number;
  open: boolean;
  rows: PhaseRow[];
}

/**
 * Each phase's list, in the tree's order (`phasesOf`): every step of the tree a job of this kind can have, never the
 * start (every job has it) nor the end, Project Complete, the special last step that comes with Closeout or Handover
 * (core `withEnd`), nor a step's own list (it comes with its step), never a step of the job's own (the lists are the
 * tree's). The phase under way
 * (the first whose closing gate is not done on the job, as core `landingPhase` reads it) is open.
 */
export function phaseRows(model: ProcessModel, pictures: ReadonlyMap<string, string> = new Map(), tradeName: (key: string) => string | null = () => null): PhaseGroup[] {
  const doc = model.dto.tree;
  const phases = phasesOf(doc);
  const live = model.dto.steps.filter((s) => !s.off && s.key && s.instance === null && !s.parentId);
  const onJob = new Map(live.map((s) => [s.key!, s]));
  const copies = new Set(model.dto.steps.filter((s) => !s.off && s.key && s.instance !== null).map((s) => s.key!));
  const out = new Set(model.dto.out);
  const history = new Set(model.dto.steps.filter((s) => s.hasHistory).map((s) => s.id));
  const done = (key: string | null | undefined) => (key ? onJob.get(key)?.state === 'done' : false);
  const lastClosed = phases.map((ph) => done(ph.closedBy)).lastIndexOf(true);
  const under = phases[lastClosed + 1]?.key ?? phases.at(-1)?.key;
  const rows = new Map<PhaseKey, PhaseRow[]>(phases.map((ph) => [ph.key, []]));
  const byOrder = [...doc.nodes].sort((a, b) => (model.tree.order.get(a.key) ?? 0) - (model.tree.order.get(b.key) ?? 0));
  for (const n of byOrder) {
    if (n.parent !== undefined || n.key === doc.start || n.key === BUILDER_END || !availableTo(n, model.process.kind)) continue;
    const phase = phaseOfNode(doc, n.key);
    if (!phase || !rows.has(phase)) continue;
    const s = onJob.get(n.key);
    const repeatable = Boolean(n.repeatable);
    const state = s || (repeatable && copies.has(n.key)) ? 'on' : out.has(n.key) ? 'left out' : 'off';
    const blocked = s ? (planDisable(model.process, s.id, (id) => history.has(id)).blocked ?? null) : null;
    rows.get(phase)!.push({
      key: n.key,
      title: n.title,
      who: treeStepBand(model, n.key, tradeName)?.word ?? '',
      picture: pictures.get(n.key) ?? null,
      state,
      blocked,
      stepId: s?.id ?? null,
      repeatable,
    });
  }
  return phases.map((ph) => {
    const list = rows.get(ph.key)!;
    return { key: ph.key, name: ph.name, on: list.filter((r) => r.state === 'on').length, of: list.length, open: ph.key === under, rows: list };
  });
}

/** A step a tick would add: asked (ticked), or needed by one asked; where it lands, late or not, and left out or not. */
export interface TickAdd {
  key: string;
  title: string;
  /** The phase it lands in (core `landingPhase`). */
  phase: PhaseKey;
  late: boolean;
  /** Left out of the job: adding puts it back as it was. */
  filed: boolean;
  asked: boolean;
}

/**
 * What ticking `keys` would add, before anything is added (q-r2-setup A): the steps asked that the job has not got, then
 * every step of the tree they need, all the way back, that it has not got (core `missingNeeds`), in the tree's order,
 * each once, a step left out among them; and whether any lands late, so the add asks NSB's reason.
 */
export function tickPreview(model: ProcessModel, keys: readonly string[]): { adds: TickAdd[]; late: boolean; end: boolean } {
  const has = new Set(model.dto.steps.filter((s) => !s.off && s.key && s.instance === null).map((s) => s.key!));
  const asked = [...new Set(keys)].filter((k) => !has.has(k) && model.tree.node(k));
  const out = new Set(model.dto.out);
  const add = (key: string, isAsked: boolean): TickAdd => {
    const land = model.landing(key);
    return { key, title: model.tree.node(key)!.title, phase: land.phase, late: land.late, filed: out.has(key), asked: isAsked };
  };
  const adds = [...asked.map((k) => add(k, true)), ...missingNeeds(model.tree, model.process, asked).map((n) => add(n.key, false))];
  return { adds, late: adds.some((a) => a.late), end: endComes(model, adds) };
}


/**
 * What a tick's preview says (the plan's words, design study 31's mock): "Engineering needs 1 step this job hasn’t got:",
 * "Add 2 steps", "Only Engineering"; several asked (Tick all), "These need …" and "Only these"; asking nothing more than
 * a reason, "Engineering comes late:". `count` is how many Add adds: the steps asked and the needs left ticked.
 */
export function previewWords(adds: readonly TickAdd[], count: number): { heading: string; add: string; only: string } {
  const asked = adds.filter((a) => a.asked);
  const needs = adds.length - asked.length;
  const one = asked.length === 1 ? titleCase(asked[0]!.title) : null;
  const steps = (n: number) => `${n} ${n === 1 ? 'step' : 'steps'}`;
  const heading = needs ? `${one ?? 'These'} ${one ? 'needs' : 'need'} ${steps(needs)} this job hasn’t got:` : `${one ?? 'These'} ${one ? 'comes' : 'come'} late:`;
  return { heading, add: `Add ${steps(count)}`, only: one ? `Only ${one}` : 'Only these' };
}

/** Whether adding these brings Project Complete, the special last step (core `bringsEnd`), the job not having it yet. */
export function endComes(model: ProcessModel, adds: readonly Pick<TickAdd, 'key'>[]): boolean {
  const has = model.dto.steps.some((s) => s.key === BUILDER_END && s.instance === null && !s.off);
  return !has && adds.some((a) => bringsEnd(model.tree, a.key));
}

/** What a tick asked for: the steps ticked, and whether by Tick all on a phase's heading or on one row. */
export interface Asking {
  keys: string[];
  all: boolean;
}

/**
 * Where a tick's preview shows (the review of 2026-10-29: a step can share its phase's key, Closeout in Close out): under
 * its phase's heading for Tick all, under the row ticked otherwise; one place, never both.
 */
export const previewAt = (asking: Asking | null, at: { phase: string } | { row: string }): boolean =>
  asking !== null && ('phase' in at ? asking.all : !asking.all && asking.keys[0] === at.row);
